import React, { useMemo, useEffect, useState } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'

import { fetchMachinesBasic } from '../actions/machines'
import { fetchSales } from '../actions/sales'

import ErrorMessage from '../components/ErrorMessage'
import { Phone } from 'lucide-react'
import LoadingProgress from '../components/LoadingProgress'
import WelcomeBlockedView from '../components/WelcomeBlockedView'

// styles specific to NewHomeView
import '../styles/NewHomeView.css'

// Recharts v3
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts'


// ---------- helpers ----------

function formatLocalYMD(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// helper to render LY comparison row with triangles, percent and value delta
// deltaPct: percentage change (cur-ly)/ly
// deltaAbs: absolute difference (cur-ly)
// invert: treat positive as bad (used for time metric)
// opts.type: 'currency'|'points'|'percent'|'number' (default)
// opts.lyValue: if provided and opts.showLy is true, show the LY value instead
//   of the difference; this is handy for occupancy where percent points are
//   less meaningful than the actual LY occupation percentage.
function renderDeltaRow(deltaPct, deltaAbs, invert = false, opts = {}) {
  if (deltaPct === null || deltaPct === undefined || deltaAbs === null || deltaAbs === undefined) {
    return null
  }
  const positive = deltaAbs > 0
  const color = positive
    ? (invert ? "text-danger" : "text-success")
    : deltaAbs < 0
      ? (invert ? "text-success" : "text-danger")
      : "text-muted"
  const arrow = deltaAbs > 0 ? "▲" : deltaAbs < 0 ? "▼" : ""
  let pctDisplay = deltaPct !== null ? `${(deltaPct * 100).toFixed(1)}%` : "—"
  let absDisplay = ""
  if (opts.showLy && opts.lyValue != null) {
    // display the raw LY value instead of the difference
    if (opts.type === "percent") {
      absDisplay = ` (LY: ${(opts.lyValue * 100).toFixed(1)}%)`
    } else if (opts.type === "currency") {
      absDisplay = ` (LY: €${opts.lyValue.toFixed(2)})`
    } else {
      absDisplay = ` (LY: ${opts.lyValue})`
    }
  } else if (opts.type === "currency") {
    const absVal = Math.abs(deltaAbs)
    absDisplay = ` (${deltaAbs >= 0 ? "+" : "-"}€${absVal.toFixed(2)})`
  } else if (opts.type === "points") {
    const pts = (deltaAbs * 100).toFixed(2)
    absDisplay = ` (${deltaAbs >= 0 ? "+" : ""}${pts} pp)`
  } else if (opts.type === "percent") {
    // show percentage points difference rounded to 1 decimal
    const pts = (deltaAbs * 100).toFixed(1)
    absDisplay = ` (${deltaAbs >= 0 ? "+" : ""}${pts} pp)`
  } else {
    // generic numeric value, round to one decimal for clarity
    const num = Number(deltaAbs.toFixed(1))
    absDisplay = ` (${deltaAbs >= 0 ? "+" : ""}${num})`
  }

  return (
    <div className="small text-muted" style={{ marginTop: 2 }}>
      <span>vs&nbsp;LY:&nbsp;</span>
      <span className={color}>
        {arrow} {pctDisplay}{absDisplay}
      </span>
    </div>
  )
}

function todayLocalYMD() {
  return formatLocalYMD(new Date())
}

// Put this near your helpers
const HOURS_24 = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)

function computeHourlyRevenueFromSales(sales = []) {
  // prefill every hour with 0
  const byHour = new Map(HOURS_24.map(h => [h, 0]))

  for (const sale of sales) {
    const d = new Date(sale.TimeStamp || sale.timestamp || sale.createdAt || Date.now())
    const hour = `${String(d.getHours()).padStart(2, '0')}:00`
    const amount = Number(sale.Amount) || 0
    byHour.set(hour, (byHour.get(hour) || 0) + amount)
  }

  // return in 00:00 → 23:00 order, ensuring all hours exist
  return HOURS_24.map(h => ({
    hour: h,
    revenue: Math.round((byHour.get(h) || 0) * 100) / 100
  }))
}

// translate raw locker type codes into a human‑friendly label similar to
// the function used elsewhere in the app.  We also inspect the sale record
// to apply the BAQUEIRA-specific mapping when needed.  Crucially, if we
// don’t recognise a code we now return it verbatim instead of the generic
// “NONE” string so the donut shows what was actually received and you can
// diagnose missing mappings.
function getTypeName(type, category = '') {
  if (!type) return 'UNKNOWN'

  if (category === 'BAQUEIRA') {
    // most Baqueira sales use the special 3XL codes, but occasional generic
    // types like BNS/BNM still appear – treat them the same as in the general
    // mapping so the donut label is readable.
    switch (type) {
      case 'BN3XL':
        return '2 PAX'
      case 'CN3XL':
        return '3 PAX'
      case 'BW3XL':
        return '4 PAX'
      case 'BNS':
        return 'Standard'
      case 'BNM':
        return 'Medium'
      default:
        return type // fall back to raw code
    }
  } else {
    switch (type) {
      case 'BNS':
        return 'Standard'
      case 'CNS':
        return 'Standard Carga'
      case 'BWS':
        return 'Standard XL'
      case 'CWS':
        return 'Standard Carga XL'
      case 'BNM':
        return 'Medium'
      case 'CNM':
        return 'Medium Carga'
      case 'BWM':
        return 'Medium XL'
      case 'CWM':
        return 'Medium Carga XL'
      case 'BNL':
        return 'Large'
      case 'CNL':
        return 'Large Carga'
      case 'BWL':
        return 'Large XL'
      case 'CWL':
        return 'Large Carga XL'
      case 'BNXL':
        return 'Large 4'
      case 'CNXL':
        return 'Large 4 Carga'
      case 'BWXL':
        return 'Large 4 XL'
      case 'CWXL':
        return 'Large 4 Carga XL'
      case 'BN2XL':
        return 'Large 5'
      case 'CN2XL':
        return 'Large 5 Carga'
      case 'BW2XL':
        return 'Large 5 XL'
      case 'CW2XL':
        return 'Large 5 Carga XL'
      case 'BN3XL':
        return 'Ski'
      case 'CN3XL':
        return 'Ski Carga'
      case 'BW3XL':
        return 'Ski XL'
      case 'CW3XL':
        return 'Ski Carga XL'
      // Voucher/Coupon Types (X and W, no LOCKER prefix)
      case 'XNS':
        return 'Standard Cupón'
      case 'XWS':
        return 'Standard XL Cupón'
      case 'XNM':
        return 'Medium Cupón'
      case 'XWM':
        return 'Medium XL Cupón'
      case 'XNL':
        return 'Large Cupón'
      case 'XWL':
        return 'Large XL Cupón'
      case 'XNXL':
        return 'Large 4 Cupón'
      case 'XWXL':
        return 'Large 4 XL Cupón'
      case 'XN2XL':
        return 'Large 5 Cupón'
      case 'XW2XL':
        return 'Large 5 XL Cupón'
      case 'XN3XL':
        return 'Ski Cupón'
      case 'XW3XL':
        return 'Ski XL Cupón'
      default:
        return type // again, show raw code
    }
  }
}

function NewHomeView(props) {
  const { machines, sales, auth } = props

  // ----- state + effects MUST be before any return -----
  const [todaySalesLocal, setTodaySalesLocal] = useState([])
  const [lySalesLocal, setLySalesLocal] = useState([])
  const [dataFetched, setDataFetched] = useState(false) // indicate when both sales fetches have completed


  // Fetch today's sales and last-year-day once
  useEffect(() => {
    const today = todayLocalYMD()
    const [y, m, d] = today.split('-')
    const ly = `${String(Number(y) - 1).padStart(4, '0')}-${m}-${d}`

    props.fetchMachinesBasic()
    const p1 = props
      .fetchSales('', '', today, today, '', '', '', '')
      .then(act => {
        const arr = (act && act.response) || []
        setTodaySalesLocal(Array.isArray(arr) ? arr : [])
      })
    const p2 = props
      .fetchSales('', '', ly, ly, '', '', '', '')
      .then(act => {
        const arr = (act && act.response) || []
        setLySalesLocal(Array.isArray(arr) ? arr : [])
      })

    Promise.all([p1, p2]).finally(() => setDataFetched(true))
  }, [])

  // Safe fallbacks (not hooks)
  const allMachines = machines?.machines || []

  // ----- computed values (hooks, still before any early return) -----
  const todayYMD = todayLocalYMD()

  const todaySales = todaySalesLocal
  const lySales = lySalesLocal

  // calculate gross totals and devolutions separately. grossAmount only
  // adds positive values (item prices or amount), while returnsAmount
  // accumulates the negative sales. The daily GMV shown on the dashboard
  // should be **net** (gross plus returns) to match the report, which
  // includes refunds as negative entries. We keep the two values handy
  // for any future breakdowns but combine them for the main total.
  const grossAmount = useMemo(() => {
    return todaySales.reduce((a, s) => {
      if (Array.isArray(s.Items) && s.Items.length) {
        return a + s.Items.reduce((sum, it) => sum + (Number(it.price) || 0), 0)
      }
      return a + Math.max(Number(s.Amount) || 0, 0)
    }, 0)
  }, [todaySales])
  // negative numbers only
  const returnsAmount = useMemo(() => {
    return todaySales.reduce((a, s) => a + Math.min(Number(s.Amount) || 0, 0), 0)
  }, [todaySales])
  const dayAmount = grossAmount + returnsAmount // net GMV including refunds

  const lyGrossAmount = useMemo(() => {
    return lySales.reduce((a, s) => {
      if (Array.isArray(s.Items) && s.Items.length) {
        return a + s.Items.reduce((sum, it) => sum + (Number(it.price) || 0), 0)
      }
      return a + Math.max(Number(s.Amount) || 0, 0)
    }, 0)
  }, [lySales])
  const lyReturnsAmount = useMemo(() => {
    return lySales.reduce((a, s) => a + Math.min(Number(s.Amount) || 0, 0), 0)
  }, [lySales])
  const lyDayAmount = lyGrossAmount + lyReturnsAmount

  const totalLockersCapacity = useMemo(() => {
    let total = 0
    for (const m of allMachines) {
      if (m?.LastIndex != null) total += Math.abs(parseInt(m.LastIndex, 10) || 0)
    }
    return total || 1 // avoid div by zero
  }, [allMachines])

  // KPI 1: occupation = Σ count / totalLockers
  const occupationDay = useMemo(
    () => (todaySales.length / totalLockersCapacity) || 0,
    [todaySales, totalLockersCapacity]
  )

  const lyOccupationDay = useMemo(
    () => (lySales.length / totalLockersCapacity) || 0,
    [lySales, totalLockersCapacity]
  )

  // common percentage delta helper (must exist before any use)
  const pctDelta = (cur, ly) => {
    if (ly === 0 || ly == null) return null
    return (cur - ly) / (ly || 1)
  }

  // additional deltas used by new KPI cards (percent change)
  const occupancyDelta = pctDelta(occupationDay, lyOccupationDay)
  const gmvDelta = pctDelta(dayAmount, lyDayAmount)

  // also compute absolute differences for display
  const occupancyAbs = occupationDay - lyOccupationDay
  const gmvAbs = dayAmount - lyDayAmount

  // average operation time in seconds — use only today's sales for the
  // current value (allSales previously included historical data causing the
  // metric to never change).  LY still derives from lySales as before.
  const avgSec = useMemo(() => {
    const times = todaySales.map(s => Number(s.OperationTime) || 0)
    const sum = times.reduce((a, b) => a + b, 0)
    return times.length ? Math.round(sum / times.length) : 0
  }, [todaySales])

  const lyAvgSec = useMemo(() => {
    const times = lySales.map(s => Number(s.OperationTime) || 0)
    const sum = times.reduce((a, b) => a + b, 0)
    return times.length ? Math.round(sum / times.length) : 0
  }, [lySales])

  const avgOpDelta = pctDelta(avgSec, lyAvgSec)
  const avgOpAbs = avgSec - lyAvgSec

  // keep previous avgOpLabel but derived from avgSec to avoid recalculation
  const avgOpLabel = useMemo(() => {
    const mm = String(Math.floor(avgSec / 60)).padStart(2, '0')
    const ss = String(avgSec % 60).padStart(2, '0')
    return `${mm}:${ss}`
  }, [avgSec])

  // KPI 3: payment methods
  // use a palette of harmonized but clearly distinguishable hues
  const donutColors = [
    '#0b97ff', // bright blue
    '#06b6d4', // cyan
    '#10b981', // teal/green
    '#f59e0b', // amber
    '#ec4899', // pink
    '#8b5cf6', // violet
    '#f97316', // orange
    '#ef4444', // red
    '#6366f1'  // indigo
  ]

  // Build slices; drop any method with 0 total; if grand total is 0, return []
  const paymentData = useMemo(() => {
    const map = new Map()
    for (const s of todaySales) {
      const pm = s.PaymentMethod || 'UNKNOWN'
      map.set(pm, (map.get(pm) || 0) + (Number(s.Amount) || 0))
    }
    const entries = Array.from(map.entries()).map(([name, value]) => ({ name, value: Number(value) || 0 }))
    const total = entries.reduce((a, d) => a + d.value, 0)

    if (total <= 0) return [] // nothing to show

    return entries
      .filter(d => d.value > 0)                      // ← drop zeros
      .map(d => ({ ...d, pct: (d.value / total) * 100 }))
  }, [todaySales])

  // same for last year (used for inner donut)
  const lyPaymentData = useMemo(() => {
    const map = new Map()
    for (const s of lySales) {
      const pm = s.PaymentMethod || 'UNKNOWN'
      map.set(pm, (map.get(pm) || 0) + (Number(s.Amount) || 0))
    }
    const entries = Array.from(map.entries()).map(([name, value]) => ({ name, value: Number(value) || 0 }))
    const total = entries.reduce((a, d) => a + d.value, 0)

    if (total <= 0) return []

    return entries
      .filter(d => d.value > 0)
      .map(d => ({ ...d, pct: (d.value / total) * 100 }))
  }, [lySales])

  // Attach colors and sort desc by value for legend order (keep same ordering for both)
  const paymentLegend = useMemo(() => {
    // build legend entries enriched with last‑year info so we can display both
    const arr = paymentData
      .map((d, i) => {
        const lyEntry = lyPaymentData.find(x => x.name === d.name) || { value: 0, pct: 0 }
        return {
          ...d,
          color: donutColors[i % donutColors.length],
          lyValue: lyEntry.value,
          lyPct: lyEntry.pct
        }
      })
      .sort((a, b) => b.value - a.value)

    return arr
  }, [paymentData, lyPaymentData])

  // inner ring must follow same ordering, but only include the ly amounts
  const lyPaymentLegend = useMemo(() => {
    return paymentLegend
      .map(d => ({ name: d.name, value: d.lyValue, pct: d.lyPct, color: d.color }))
      .filter(d => d.value > 0)
  }, [paymentLegend])

  // central totals should match the overall GMV (including refunds),
  // so use the precomputed dayAmount/lyDayAmount rather than summing the
  // filtered legend values (which drop negative or zero entries).
  const paymentTotal = dayAmount
  const lyPaymentTotal = lyDayAmount

  // TPV & CASH percentages (of current month total)
  const { tpvPct, cashPct } = useMemo(() => {
    const findPct = (name) => {
      const e = paymentData.find(d => d.name === name)
      return e ? e.pct : 0
    }
    return { tpvPct: findPct('TPV'), cashPct: findPct('CASH') }
  }, [paymentData])

  // locker type breakdown (count of sales per type) for bottom donut
  const lockerTypeData = useMemo(() => {
    const map2 = new Map()
    for (const s of todaySales) {
      // determine raw type code: prefer explicit field, otherwise try first
      // item entry (many legacy sales only had item string such as
      // "LOCKERBNS").
      let raw = s.LockerType
      if (!raw && Array.isArray(s.Items) && s.Items.length) {
        const itemCode = s.Items[0].item || ''
        if (itemCode.startsWith('LOCKER')) raw = itemCode.slice('LOCKER'.length)
        else raw = itemCode
      }
      raw = raw || 'UNKNOWN'

      // convert to display label using helper; pass customer category if
      // available so BAQUEIRA-specific names are applied.
      const category = s.Customer?.Category || ''
      const display = getTypeName(raw, category)

      map2.set(display, (map2.get(display) || 0) + 1)
    }
    const entries = Array.from(map2.entries()).map(([name, value]) => ({ name, value }))
    const total = entries.reduce((a, d) => a + d.value, 0)
    if (total <= 0) return []
    return entries.map(d => ({ ...d, pct: (d.value / total) * 100 }))
  }, [todaySales])

  // mirror the above for last‑year sales so we can draw an inner ring and
  // display LY counts/pcts in the legend like we do for payments.
  const lyLockerTypeData = useMemo(() => {
    const map2 = new Map()
    for (const s of lySales) {
      let raw = s.LockerType
      if (!raw && Array.isArray(s.Items) && s.Items.length) {
        const itemCode = s.Items[0].item || ''
        if (itemCode.startsWith('LOCKER')) raw = itemCode.slice('LOCKER'.length)
        else raw = itemCode
      }
      raw = raw || 'UNKNOWN'
      const category = s.Customer?.Category || ''
      const display = getTypeName(raw, category)
      map2.set(display, (map2.get(display) || 0) + 1)
    }
    const entries = Array.from(map2.entries()).map(([name, value]) => ({ name, value }))
    const total = entries.reduce((a, d) => a + d.value, 0)
    if (total <= 0) return []
    return entries.map(d => ({ ...d, pct: (d.value / total) * 100 }))
  }, [lySales])

  const lockerTypeLegend = useMemo(() => {
    // include LY info for each slice
    const arr = lockerTypeData
      .map((d, i) => {
        const lyEntry = lyLockerTypeData.find(x => x.name === d.name) || { value: 0, pct: 0 }
        return {
          ...d,
          color: donutColors[i % donutColors.length],
          lyValue: lyEntry.value,
          lyPct: lyEntry.pct
        }
      })
      .sort((a, b) => b.value - a.value)

    return arr
  }, [lockerTypeData, lyLockerTypeData])

  const lockerTypeTotal = useMemo(() => lockerTypeLegend.reduce((a,d)=>a+d.value,0), [lockerTypeLegend])
  const lyLockerTypeTotal = useMemo(() => lockerTypeLegend.reduce((a,d)=>a+d.lyValue,0), [lockerTypeLegend])

  // build a lookup from machine id or serial to a human label; this lets
  // the leaderboard show the serial number even when the sales record only
  // contains the _id (which happens for CUSTOMER role).
  const machineIdToLabel = useMemo(() => {
    const m = new Map()
    for (const mach of allMachines) {
      const label = mach.Serial || mach.Name || mach._id || ''
      if (mach._id) m.set(String(mach._id), label)
      if (mach.Serial) m.set(String(mach.Serial), label)
    }
    return m
  }, [allMachines])

  // top 5 machines by amount today (also track count of tickets)
  const machineLeaderboard = useMemo(() => {
    const map3 = new Map()
    for (const s of todaySales) {
      let m = 'UNKNOWN'
      if (s.MachineName) m = s.MachineName
      else if (s.Machine) {
        if (typeof s.Machine === 'string') m = s.Machine
        else m = s.Machine.Serial || s.Machine._id || JSON.stringify(s.Machine)
      } else if (s.MachineId) m = s.MachineId
      // apply lookup if available
      if (machineIdToLabel.has(m)) m = machineIdToLabel.get(m)
      const amt = Number(s.Amount) || 0
      const entry = map3.get(m) || { total: 0, count: 0 }
      entry.total += amt
      entry.count += 1
      map3.set(m, entry)
    }
    const arr = Array.from(map3.entries()).map(([name, { total, count }]) => ({ name, total, count }))
    return arr.sort((a, b) => b.total - a.total).slice(0, 5)
  }, [todaySales, machineIdToLabel])

  // last-year machine stats for comparisons
  const machineLeaderboardLy = useMemo(() => {
    const map3 = new Map()
    for (const s of lySales) {
      let m = 'UNKNOWN'
      if (s.MachineName) m = s.MachineName
      else if (s.Machine) {
        if (typeof s.Machine === 'string') m = s.Machine
        else m = s.Machine.Serial || s.Machine._id || JSON.stringify(s.Machine)
      } else if (s.MachineId) m = s.MachineId
      if (machineIdToLabel.has(m)) m = machineIdToLabel.get(m)
      const amt = Number(s.Amount) || 0
      const entry = map3.get(m) || { total: 0, count: 0 }
      entry.total += amt
      entry.count += 1
      map3.set(m, entry)
    }
    const arr = Array.from(map3.entries()).map(([name, { total, count }]) => ({ name, total, count }))
    return arr.sort((a, b) => b.total - a.total).slice(0, 5)
  }, [lySales, machineIdToLabel])

  // merge LY data into today’s leaderboard for easy rendering
  const machineLeaderboardMerged = useMemo(() => {
    const lyMap = new Map(machineLeaderboardLy.map(x => [x.name, x]))
    return machineLeaderboard.map(m => {
      const ly = lyMap.get(m.name) || { total: 0, count: 0 }
      return {
        ...m,
        lyTotal: ly.total,
        lyCount: ly.count
      }
    })
  }, [machineLeaderboard, machineLeaderboardLy])

  // Row 2 — left: hourly revenue + peak hour (derive with useMemo to avoid extra hook)
  // use today's sales only – the previous `allSales` list contained historical
  // records and could lead to misleading values or stale peaks.
  const hourlySeries = useMemo(() => computeHourlyRevenueFromSales(todaySales), [todaySales])
  const peakHour = useMemo(() => {
    if (!hourlySeries.length) return null
    return hourlySeries.reduce((acc, p) => (p.revenue > (acc?.revenue ?? -Infinity) ? p : acc), null)?.hour ?? null
  }, [hourlySeries])

  // compute hourly breakdown and peak for today alone
  const hourlySeriesDay = useMemo(() => computeHourlyRevenueFromSales(todaySales), [todaySales])
  const peakHourDay = useMemo(() => {
    if (!hourlySeriesDay.length) return null
    return hourlySeriesDay.reduce((acc, p) => (p.revenue > (acc?.revenue ?? -Infinity) ? p : acc), null)?.hour ?? null
  }, [hourlySeriesDay])

  // LY hourly breakdown & peak
  const hourlySeriesLyDay = useMemo(() => computeHourlyRevenueFromSales(lySales), [lySales])
  const lyPeakHourDay = useMemo(() => {
    if (!hourlySeriesLyDay.length) return null
    return hourlySeriesLyDay.reduce((acc, p) => (p.revenue > (acc?.revenue ?? -Infinity) ? p : acc), null)?.hour ?? null
  }, [hourlySeriesLyDay])

  // merge both series so recharts sees two lines
  const mergedHourly = useMemo(() => {
    const map = new Map()
    for (const p of hourlySeriesDay) {
      map.set(p.hour, { hour: p.hour, revenue: p.revenue, revenueLy: 0 })
    }
    for (const p of hourlySeriesLyDay) {
      const existing = map.get(p.hour)
      if (existing) existing.revenueLy = p.revenue
      else map.set(p.hour, { hour: p.hour, revenue: 0, revenueLy: p.revenue })
    }
    return Array.from(map.values()).sort((a,b)=>a.hour.localeCompare(b.hour))
  }, [hourlySeriesDay, hourlySeriesLyDay])

  const todayCount = todaySales.length
  const lyCount = lySales.length
  const todayAvg = todayCount ? (dayAmount / todayCount) : 0
  const lyAvg = lyCount ? (lyDayAmount / lyCount) : 0
  const todayGMVPerLocker = totalLockersCapacity ? (dayAmount / totalLockersCapacity) : 0
  const lyGMVPerLocker = totalLockersCapacity ? (lyDayAmount / totalLockersCapacity) : 0

  const countDelta = pctDelta(todayCount, lyCount)
  const avgDelta = pctDelta(todayAvg, lyAvg)
  const gmvpDelta = pctDelta(todayGMVPerLocker, lyGMVPerLocker)
  // note: other deltas already computed above (occupancyDelta, gmvDelta, avgOpDelta)


  // Row 2 — right: machine ranking by amount
  const machineRanking = useMemo(() => {
    const map = new Map()
    for (const s of todaySales) {
      const serial = s?.Machine?.Serial || 'UNKNOWN'
      map.set(serial, (map.get(serial) || 0) + (Number(s.Amount) || 0))
    }
    return Array.from(map.entries())
      .map(([serial, total]) => ({ serial, total }))
      .sort((a, b) => b.total - a.total)
  }, [todaySales])

  // Fancy title date in es-ES
  const fancyDate = new Date().toLocaleDateString('es-ES', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  })


  // ----- early returns AFTER all hooks -----
  if (machines?.error) return <ErrorMessage message={machines.error.message} />
  if (sales?.error) return <ErrorMessage message={sales.error.message} />

  // Fully blocked users
  if (auth.type === "PRODUCCION" || auth.type === "PROVEEDOR" || auth.type === "PROGRAMMER") {
    return <WelcomeBlockedView />
  }

  // PDA users go straight to the point-of-sale view
  if (auth.type === "PDA") {
    return <Redirect to="/lockersale" />
  }

  const isLoading =
    !machines?.machines || machines.loading ||
    !sales?.sales || sales.loading

  if (isLoading) {
    return <LoadingProgress />
  }

  return (
    <div className="kpi-page">
      {/* header for KPI page – scoped styles in NewHomeView.css */}
      <div className="kpi-page-header mb-3">
        <div className="kpi-title-section">
          <h1 className="kpi-title">
            <i className="fas fa-chart-line"></i> Dashboard del día
          </h1>
          <div className="small text-muted mt-1">{fancyDate}</div>
        </div>
      </div>

      {/* if there really aren’t any sales, let the user know – daily filter is deliberate */}
      {dataFetched && todayCount === 0 && lyCount === 0 && (
        <div className="alert alert-info">
          No hay ventas registradas para hoy ni para la misma fecha del año pasado. Si esperabas
          ver datos, comprueba el filtro de fechas en la base o selecciona otro día.
        </div>
      )}

      {/* -------- Row 1b: today's sales KPIs (operational-style with icons & LY comparison) -------- */}
      <div className="kpi-row mb-3">
        {/* total lockers */}
        <div className="kpi-card kpi-accent-total">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-lock" /></div>
              <div className="kpi-card-title">Taquillas</div>
            </div>
            <div className="kpi-value">{totalLockersCapacity}</div>
            {renderDeltaRow(null, null)}
          </div>
        </div>

        {/* occupation */}
        <div className="kpi-card kpi-accent-occupancy">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-percent" /></div>
              <div className="kpi-card-title">Ocupación</div>
            </div>
            <div className="kpi-value">{(occupationDay * 100).toFixed(1)}%</div>
            {renderDeltaRow(occupancyDelta, occupancyAbs, false, {type:'percent', lyValue: lyOccupationDay, showLy: true})}
          </div>
        </div>

        {/* total GMV */}
        <div className="kpi-card kpi-accent-income-day">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-euro-sign" /></div>
              <div className="kpi-card-title">GMV total</div>
            </div>
            <div className="kpi-value">{dayAmount.toFixed(2)} €</div>
            {renderDeltaRow(gmvDelta, gmvAbs, false, {type:'currency'})}
          </div>
        </div>

        {/* average operation time */}
        <div className="kpi-card kpi-accent-time">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-stopwatch" /></div>
              <div className="kpi-card-title">Tiempo medio</div>
            </div>
            <div className="kpi-value">{avgOpLabel}</div>
            {renderDeltaRow(avgOpDelta, avgOpAbs, true, {type:'number'})}
          </div>
        </div>

        {/* maintain previous KPIs if still useful (sales count, ticket, GMV/locker, peak) */}
        <div className="kpi-card kpi-accent-income-day">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-shopping-cart" /></div>
              <div className="kpi-card-title">Transacciones</div>
            </div>
            <div className="kpi-value">{todayCount}</div>
            {renderDeltaRow(countDelta, todayCount - lyCount, false, {type:'number'})}
          </div>
        </div>

        <div className="kpi-card kpi-accent-income-month">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-ticket-alt" /></div>
              <div className="kpi-card-title">Ticket med.</div>
            </div>
            <div className="kpi-value">{todayAvg.toFixed(2)} €</div>
            {renderDeltaRow(avgDelta, todayAvg - lyAvg, false, {type:'currency'})}
          </div>
        </div>

        <div className="kpi-card kpi-accent-occupancy-month">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-lock" /></div>
              <div className="kpi-card-title">GMV x taquilla</div>
            </div>
            <div className="kpi-value">{todayGMVPerLocker.toFixed(2)} €</div>
            {renderDeltaRow(gmvpDelta, todayGMVPerLocker - lyGMVPerLocker, false, {type:'currency'})}
          </div>
        </div>

        {/* <div className="kpi-card kpi-accent-income-month">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-clock" /></div>
              <div className="kpi-card-title">Hora pico</div>
            </div>
            <div className="kpi-value">{peakHourDay || '—'}</div>
            {lyPeakHourDay && (
              <div className="small text-muted" style={{ marginTop: 2 }}>
                vs&nbsp;LY:&nbsp;<strong>{lyPeakHourDay}</strong>
              </div>
            )}
          </div>
        </div> */}
      </div>

      {/* -------- Row 2: 2/3 + 1/3 -------- */}
      <div className="row g-3 align-items-stretch">
        {/* Left: hourly revenue chart + peak hour (narrower) */}
        <div className="col-12 col-lg-7 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-clock mr-2"></i>
                  <div className="kpi-section-title m-0">Ingreso por hora (hoy)</div>
                </div>
                {peakHourDay && (
                  <span className="kpi-peak">
                    Hora pico: <strong>{peakHourDay}</strong>
                    {lyPeakHourDay && (
                      <> / LY <strong>{lyPeakHourDay}</strong></>
                    )}
                  </span>
                )}
              </div>
              <div className="kpi-chart" style={{ flex: 1, display: 'flex' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mergedHourly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="hour"
                      ticks={HOURS_24}
                      interval={0}            // show every hour tick
                      tick={{ fontSize: 12 }}
                      tickMargin={8}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => [`${v} €`, 'Ingreso']} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" name="Ingreso" stroke="#0b97ff" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="revenueLy" name="Ingreso LY" stroke="#ff9800" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Right: ranking by machine */}
        {/* Right: info + donut, matched height (wider) */}
        <div className="col-12 col-lg-5 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100 kpi-bg-dark">
            <div className="card-body d-flex flex-column">


              {/* Donut panel fills remaining height */}
              {/* content sits directly in card-body now */}
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-credit-card mr-2"></i>
                    <div className="kpi-section-title m-0">Método de pago</div>
                  </div>

                  {paymentLegend.length > 0 ? (
                    <div className="kpi-donut-row mt-4">
                      <div className="kpi-donut-chart" style={{ position: 'relative' }}>
                        {/* raised height to give more space for larger doughnut */}
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            {/* outer ring: current payments */}
                            <Pie
                              data={paymentLegend}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={75}    // larger inner radius for more inner space
                              outerRadius={105}   // enlarge outer radius to make overall donut bigger
                              paddingAngle={2}
                              label={({ index }) => {
                                const entry = paymentLegend[index] || {};
                                return entry.pct ? `${entry.pct.toFixed(1)}%` : '';
                              }}
                              labelLine={{
                                stroke: '#6b7280',
                                strokeDasharray: '3 4',
                                strokeWidth: 1,
                              }}
                            >
                              {paymentLegend.map((entry, idx) => (
                                <Cell key={entry.name} fill={entry.color} />
                              ))}
                            </Pie>
                            {/* inner ring: last year distribution */}
                            {lyPaymentLegend.length > 0 && (
                              <Pie
                                data={lyPaymentLegend}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={50}  // make inner ring larger and thicker
                                outerRadius={70}
                                paddingAngle={1}
                                label={false}
                                labelLine={false}
                              >
                                {lyPaymentLegend.map((entry, idx) => (
                                  <Cell
                                    key={entry.name}
                                    fill={entry.color}
                                    fillOpacity={0.5}
                                  />
                                ))}
                              </Pie>
                            )}
                            <Tooltip
                              formatter={(value, name) => [
                                `${Number(value).toFixed(2)} € (${dayAmount > 0 ? ((value / dayAmount) * 100).toFixed(1) : 0}%)`,
                                name,
                              ]}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        {/* central total overlay */}
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            zIndex: 1,
                            color: '#000000'
                          }}
                        >
                          <div style={{ fontSize: 16, fontWeight: 700 }}>
                            {dayAmount.toFixed(2)} €
                          </div>
                          {lyPaymentLegend.length > 0 && (
                            <div style={{ fontSize: 12, color: '#d1d5db', marginTop: 2 }}>
                              vs LY: {lyDayAmount.toFixed(2)} €
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="kpi-donut-legend">
                        <ul>
                          {paymentLegend.map((d) => (
                            <li key={d.name} className="kpi-legend-item">
                              <span className="d-flex align-items-center gap-5">
                                <svg className="mr-1" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                                  <circle cx="5" cy="5" r="5" fill={d.color} />
                                </svg>
                                <span className="kpi-legend-label"> {d.name}</span>
                              </span>
                              <div className="kpi-legend-value">
                                <span>{d.pct.toFixed(1)}% • {d.value.toFixed(2)}€</span>
                                {d.lyValue > 0 && (
                                  <span className="kpi-ly-info">
                                    vs LY: ({d.lyPct.toFixed(1)}%) · {d.lyValue.toFixed(2)}€
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="kpi-donut-empty">Sin datos de ventas</div>
                  )}

            </div>
          </div>
        </div>

      </div>

      {/* -------- Row 3: locker types donut + top machines -------- */}
      <div className="row g-3 align-items-stretch mt-3">
        {/* locker type breakdown */}
        <div className="col-12 col-lg-6 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100 kpi-bg-dark">
            <div className="card-body d-flex flex-column">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-layer-group mr-2"></i>
                <div className="kpi-section-title m-0">Tipos de taquilla</div>
              </div>

              {lockerTypeLegend.length > 0 ? (
                <div className="kpi-donut-row mt-4">
                  <div className="kpi-donut-chart" style={{ position: 'relative' }}>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={lockerTypeLegend}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={75}
                          outerRadius={105}
                          paddingAngle={2}
                          label={({ index }) => {
                            const entry = lockerTypeLegend[index] || {}
                            return entry.pct ? `${entry.pct.toFixed(1)}%` : ''
                          }}
                          labelLine={{
                            stroke: '#6b7280',
                            strokeDasharray: '3 4',
                            strokeWidth: 1,
                          }}
                        >
                          {lockerTypeLegend.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        {/* inner year ring if we have any LY counts */}
                        {lyLockerTypeTotal > 0 && (
                          <Pie
                            data={lockerTypeLegend.map(d => ({ name: d.name, value: d.lyValue, pct: d.lyPct, color: d.color }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={70}
                            paddingAngle={1}
                            label={false}
                            labelLine={false}
                          >
                            {lockerTypeLegend.map((entry) => (
                              <Cell
                                key={entry.name}
                                fill={entry.color}
                                fillOpacity={0.5}
                              />
                            ))}
                          </Pie>
                        )}
                        <Tooltip
                          formatter={(value, name) => [
                            `${value} (${lockerTypeTotal > 0 ? ((value / lockerTypeTotal) * 100).toFixed(1) : 0}%)`,
                            name,
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        zIndex: 1,
                        color: '#000000'
                      }}
                    >
                      <div style={{ fontSize: 16, fontWeight: 700 }}>
                        {lockerTypeTotal}
                      </div>
                      {lyLockerTypeTotal > 0 && (
                        <div style={{ fontSize: 12, color: '#d1d5db', marginTop: 2 }}>
                          vs LY: {lyLockerTypeTotal}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="kpi-donut-legend">
                    <ul>
                      {lockerTypeLegend.map((d) => (
                        <li key={d.name} className="kpi-legend-item">
                          <span className="d-flex align-items-center gap-2">
                            <svg className="mr-1" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                              <circle cx="5" cy="5" r="5" fill={d.color} />
                            </svg>
                            <span className="kpi-legend-label"> {d.name}</span>
                          </span>
                          <div className="kpi-legend-value">
                            <span>{d.pct.toFixed(1)}% • {d.value}</span>
                            {d.lyValue > 0 && (
                              <span className="kpi-ly-info">
                                vs LY: ({d.lyPct.toFixed(1)}%) · {d.lyValue}
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="kpi-donut-empty">Sin datos</div>
              )}
            </div>
          </div>
        </div>

        {/* top machines list */}
        <div className="col-12 col-lg-6 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-microchip mr-2"></i>
                <div className="kpi-section-title m-0">Top máquinas</div>
              </div>

              {machineLeaderboardMerged.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Máquina</th>
                        <th className="text-end">Transacciones</th>
                        <th className="text-end">Ventas</th>
                        <th className="text-end">Avg ticket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {machineLeaderboardMerged.map(m => (
                        <tr key={m.name} className="modern-list-item">
                          <td><small className="text-muted">{m.name}</small></td>
                          <td className="text-end">
                            {m.count}
                            {m.lyCount != null && (
                              <div className="small text-muted">vs LY: {m.lyCount}</div>
                            )}
                          </td>
                          <td className="text-end">
                            {m.total.toFixed(2)} €
                            {m.lyTotal != null && (
                              <div className="small text-muted">vs LY: {m.lyTotal.toFixed(2)} €</div>
                            )}
                          </td>
                          <td className="text-end">
                            {(m.total/m.count).toFixed(2)} €
                            {m.lyCount ? (
                              <div className="small text-muted">
                                vs LY: {(m.lyTotal/m.lyCount||0).toFixed(2)} €
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted">No hay máquinas</div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

const mapStateToProps = state => ({
  machines: state.machines,
  sales: state.sales,
  auth: state.auth
})

const mapDispatchToProps = dispatch => ({
  fetchMachinesBasic: bindActionCreators(fetchMachinesBasic, dispatch),
  fetchSales: bindActionCreators(fetchSales, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(NewHomeView)
